import { makeStyles } from "tss-react/mui";
import { PRINT_ORIENTATION } from "@miplatform/constants";
import { getRootUrl } from "@miplatform/utils";
import { Box } from "@mui/material";
import { MiPrintFooter } from "./MiPrintFooter";
import { MiPrintHeader } from "./MiPrintHeader";

/**
 * MiPrintTemplate component for rendering a printable document with a header, content, and footer.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} [props.account={}] - Account details including company info, contact details, and licenses.
 * @param {String} [props.orientation=PRINT_ORIENTATION.PORTRAIT] - Orientation of the template (portrait or landscape).
 * @param {Array<{ label: string, value: string }>} [props.headerFields=[]] - Fields to be displayed in the header.
 * @param {Object} [props.highlightedHeaderField=null] - Optional highlighted field in the header.
 * @param {Boolean} [props.hideWatermark=false] - Flag to hide the watermark.
 * @param {Boolean} [props.hideFooter=false] - Flag to hide the footer.
 * @param {React.ReactNode} props.children - Content to be rendered inside the template.
 * @returns {JSX.Element} The rendered MiPrintTemplate component.
 */
export const MiPrintTemplate = ({
  account = {},
  orientation = PRINT_ORIENTATION.PORTRAIT,
  headerFields = [],
  highlightedHeaderField = null,
  statusHeaderField = null,
  showPrintedDate = false,
  hideWatermark,
  hideFooter,
  hideHeader,
  children,
}) => {
  const API_URL = getRootUrl();
  const {
    legalName,
    phoneNumber,
    facsimileNumber,
    website,
    email,
    address,
    watermark,
    logo,
    licenses,
    invoiceTermsAndConditionsHtml,
  } = account;
  const { classes } = useStyles({ orientation });

  return (
    <Box className={classes.root}>
      {watermark && !hideWatermark && (
        <img
          className={classes.watermark}
          src={`${API_URL}/${watermark?.referenceId}`}
          alt="watermark"
        />
      )}
      {!hideHeader && (
        <MiPrintHeader
          account={{
            legalName,
            phoneNumber,
            facsimileNumber,
            website,
            email,
            address,
            logo,
          }}
          headerFields={headerFields}
          highlightedHeaderField={highlightedHeaderField}
          statusHeaderField={statusHeaderField}
          showPrintedDate={showPrintedDate}
        />
      )}
      <Box className={classes.content}>{children}</Box>
      {!hideFooter && (
        <MiPrintFooter
          invoiceTermsAndConditionsHtml={invoiceTermsAndConditionsHtml}
          licenses={licenses}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme, { orientation }) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: orientation === PRINT_ORIENTATION.PORTRAIT ? "8.5in" : "100%",
    padding: theme.spacing(6),
    paddingBottom: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    minHeight: "100vh",
    gap: theme.spacing(4),
    overflow: "hidden",
  },
  content: {
    marginTop: theme.spacing(2),
    width: "100%",
    flex: 1,
  },
  watermark: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none",
    width: "70%",
    height: "70%",
    padding: theme.spacing(6),
    objectFit: "contain",
    opacity: 0.1,
  },
}));
